import React, { useState, useEffect } from 'react';
import { Heart } from 'lucide-react';
import { likeListing, unlikeListing, isListingLiked, getListingLikesCount } from '../lib/likes';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

interface LikeButtonProps {
  listingId: string;
  showCount?: boolean;
  className?: string;
}

function LikeButton({ listingId, showCount = true, className = '' }: LikeButtonProps) {
  const { user } = useAuth();
  const [liked, setLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadLikeStatus();
    } else {
      setLoading(false);
    }
  }, [listingId, user]);

  const loadLikeStatus = async () => {
    try {
      const [{ liked, error: likedError }, { count, error: countError }] = await Promise.all([
        isListingLiked(listingId),
        getListingLikesCount(listingId)
      ]);

      if (likedError) throw likedError;
      if (countError) throw countError;

      setLiked(liked);
      setLikesCount(count);
    } catch (error) {
      console.error('Error loading like status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLikeToggle = async () => {
    if (!user) {
      toast.error('Please sign in to save listings');
      return;
    }

    try {
      const { error } = await (liked ? unlikeListing(listingId) : likeListing(listingId));
      if (error) throw error;

      setLiked(!liked);
      setLikesCount(prev => prev + (liked ? -1 : 1));
      toast.success(liked ? 'Removed from favorites' : 'Added to favorites');
    } catch (error) {
      console.error('Error toggling like:', error);
      toast.error('Failed to update favorite status');
    }
  };

  if (loading) {
    return <div className="animate-pulse h-6 w-6 bg-gray-200 rounded-full" />;
  }

  return (
    <button
      onClick={handleLikeToggle}
      className={`flex items-center space-x-1 ${className}`}
      aria-label={liked ? 'Remove from favorites' : 'Add to favorites'}
    >
      <Heart
        className={`h-6 w-6 ${
          liked
            ? 'text-rose-600 fill-current'
            : 'text-gray-400 hover:text-rose-600'
        } transition-colors`}
      />
      {showCount && (
        <span className="text-sm text-gray-600">{likesCount}</span>
      )}
    </button>
  );
}

export default LikeButton;